/**
 * 工作流 REST API 服务
 *
 * 提供工作流 CRUD、执行、导入导出等功能，替代 WebSocket。
 */

import { createSSEStream, type SSEEvent } from './sseStream';

const API_BASE = '/api/workflows';

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: '请求失败' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  return response.json();
}

export interface WorkflowResponse {
  id: number;
  workspace_id: number;
  name: string;
  description?: string;
  nodes: unknown[];
  edges: unknown[];
  created_at: string;
  updated_at: string;
}

export interface WorkflowListItem {
  id: number;
  workspace_id: number;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

export interface WorkflowListResponse {
  workflows: WorkflowListItem[];
  total: number;
}

export interface WorkflowCreateParams {
  workspace_id: number;
  name: string;
  description?: string;
  nodes?: unknown[];
  edges?: unknown[];
}

export interface WorkflowUpdateParams {
  name?: string;
  description?: string;
  nodes?: unknown[];
  edges?: unknown[];
}

export interface WorkflowValidateParams {
  nodes: unknown[];
  edges: unknown[];
}

export interface WorkflowValidateResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

export interface WorkflowImportParams {
  workspace_id: number;
  name: string;
  description?: string;
  nodes: unknown[];
  edges: unknown[];
}

export interface ExecutionHistoryItem {
  id: number;
  workflow_id: number;
  execution_id: string;
  state: string;
  debug_mode: boolean;
  total_tokens: number;
  total_duration_ms: number;
  error_message?: string;
  started_at?: string;
  completed_at?: string;
  created_at?: string;
  logs: unknown[];
}

export interface ExecutionHistoryResponse {
  executions: ExecutionHistoryItem[];
  total: number;
}

export interface ExecutionStartParams {
  debug_mode?: boolean;
  task_prompt?: string;
}

export class WorkflowApiError extends Error {
  constructor(message: string, public code?: string) {
    super(message);
    this.name = 'WorkflowApiError';
  }
}

export const workflowApi = {
  async listWorkflows(workspaceId: number): Promise<WorkflowListResponse> {
    const params = new URLSearchParams({ workspace_id: String(workspaceId) });
    const response = await fetch(`${API_BASE}?${params}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    return handleResponse<WorkflowListResponse>(response);
  },

  async createWorkflow(data: WorkflowCreateParams): Promise<WorkflowResponse> {
    const response = await fetch(API_BASE, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<WorkflowResponse>(response);
  },

  async getWorkflow(workflowId: number): Promise<WorkflowResponse> {
    const response = await fetch(`${API_BASE}/${workflowId}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    return handleResponse<WorkflowResponse>(response);
  },

  async updateWorkflow(
    workflowId: number,
    data: WorkflowUpdateParams
  ): Promise<WorkflowResponse> {
    const response = await fetch(`${API_BASE}/${workflowId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<WorkflowResponse>(response);
  },

  async deleteWorkflow(workflowId: number): Promise<void> {
    const response = await fetch(`${API_BASE}/${workflowId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    await handleResponse(response);
  },

  async exportWorkflow(workflowId: number): Promise<WorkflowResponse> {
    const response = await fetch(`${API_BASE}/${workflowId}/export`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse<WorkflowResponse>(response);
  },

  async importWorkflow(data: WorkflowImportParams): Promise<WorkflowResponse> {
    const response = await fetch(`${API_BASE}/import`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<WorkflowResponse>(response);
  },

  async validateWorkflow(
    workflowId: number,
    data: WorkflowValidateParams
  ): Promise<WorkflowValidateResult> {
    const response = await fetch(`${API_BASE}/${workflowId}/validate`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<WorkflowValidateResult>(response);
  },

  async *executeWorkflow(
    workflowId: number,
    workspaceId: number,
    data: ExecutionStartParams = {},
    signal?: AbortSignal
  ): AsyncGenerator<SSEEvent> {
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Workspace-Id': String(workspaceId),
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}/${workflowId}/execute`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
      credentials: 'include',
      signal,
    });

    if (!response.ok) {
      let errorMessage = '执行失败';
      try {
        const errData = await response.json();
        errorMessage = errData.detail || errData.message || errorMessage;
      } catch {
        // ignore
      }
      throw new WorkflowApiError(errorMessage);
    }

    if (!response.body) {
      throw new WorkflowApiError('响应体为空');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let currentEvent = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
            continue;
          }

          if (line.startsWith('data: ')) {
            const dataStr = line.slice(6).trim();
            try {
              const eventData = JSON.parse(dataStr);
              yield {
                type: currentEvent || eventData.type || 'message',
                data: eventData,
              };
              currentEvent = '';
            } catch {
              // ignore parse errors
            }
          }
        }
      }

      if (buffer.trim()) {
        const lines = buffer.split('\n');
        let currentEvent = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
            continue;
          }
          if (line.startsWith('data: ')) {
            const dataStr = line.slice(6).trim();
            try {
              const eventData = JSON.parse(dataStr);
              yield {
                type: currentEvent || eventData.type || 'message',
                data: eventData,
              };
            } catch {
              // ignore parse errors
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  },

  async stopExecution(executionId: string): Promise<{ success: boolean; state: string }> {
    const response = await fetch(`${API_BASE}/executions/${executionId}/stop`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  async pauseExecution(executionId: string): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE}/executions/${executionId}/pause`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  async resumeExecution(
    executionId: string
  ): Promise<{ success: boolean; state: string }> {
    const response = await fetch(`${API_BASE}/executions/${executionId}/resume`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  async stepExecution(executionId: string): Promise<{
    success: boolean;
    state: string;
    current_node_id?: string;
  }> {
    const response = await fetch(`${API_BASE}/executions/${executionId}/step`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  async getExecutionHistory(
    workflowId: number,
    limit: number = 10
  ): Promise<ExecutionHistoryResponse> {
    const params = new URLSearchParams({ limit: String(limit) });
    const response = await fetch(`${API_BASE}/${workflowId}/executions?${params}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    return handleResponse<ExecutionHistoryResponse>(response);
  },
};

export default workflowApi;
